import { extractPrimaryUrl, extractWorkTags, parseFrontmatter, renderMarkdownToHtml } from './markdown.js';

/**
 * Featured works live in client/src/assets/featured/<category>/
 * Categories: home, game-dev, mods, fullstack, other-dev
 *   WorkName.png|gif|jpg|mp4|...  thumbnail / media
 *   WorkName.md                   optional short description
 *                                 Game: Name and Date: Month YYYY show as tags on the card
 *   WorkName.blog.md              optional long-form markdown dev blog
 *                                 frontmatter: title, date (YYYY-MM-DD), author
 *                                 optional thumbnail: filename in the same folder
 *   WorkName.blog.png|jpg|...     optional blog-index thumbnail (not a featured work)
 */
const allFeaturedMedia = import.meta.glob(
  '../assets/featured/**/*.{png,jpg,jpeg,gif,webp,svg,mp4,webm,ogg,mp3}',
  { eager: true, query: '?url', import: 'default' }
);

const allFeaturedText = import.meta.glob(
  '../assets/featured/**/*.{txt,md,html}',
  { eager: true, query: '?raw', import: 'default' }
);

export const FEATURED_CATEGORY_PAGES = {
  home: { path: '/', label: 'Overview' },
  'game-dev': { path: '/game-dev', label: 'Game Dev' },
  mods: { path: '/game-dev', label: 'Mods' },
  fullstack: { path: '/fullstack', label: 'Fullstack' },
  'other-dev': { path: '/other-dev', label: 'Other Dev' }
};

export const DEFAULT_BLOG_AUTHOR = 'Mike Moe';

export function parseBlogDate(value) {
  if (!value || typeof value !== 'string') return null;
  const iso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatBlogDate(value) {
  const parsed = parseBlogDate(value);
  if (!parsed) return value || '';
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function getBlogDateTime(value) {
  const parsed = parseBlogDate(value);
  if (!parsed) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getBlogExcerpt(body, maxLength = 180) {
  if (!body) return '';
  const plain = body
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/[*_`>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength).replace(/\s+\S*$/, '')}...`;
}

function getExtension(url) {
  const queryIndex = url.indexOf('?');
  const clean = queryIndex >= 0 ? url.slice(0, queryIndex) : url;
  const dot = clean.lastIndexOf('.');
  return dot >= 0 ? clean.slice(dot + 1).toLowerCase() : '';
}

function getFileName(path) {
  const parts = path.split('/');
  const file = parts[parts.length - 1] || '';
  const dot = file.lastIndexOf('.');
  return dot >= 0 ? file.slice(0, dot) : file;
}

function getPathExtension(path) {
  const dot = path.lastIndexOf('.');
  return dot >= 0 ? path.slice(dot + 1).toLowerCase() : '';
}

function isBlogPath(path) {
  return path.toLowerCase().endsWith('.blog.md');
}

const BLOG_MEDIA_EXT = /\.blog\.(png|jpg|jpeg|gif|webp|svg|mp4|webm)(?:\?|$)/i;

function isBlogMediaPath(path) {
  return BLOG_MEDIA_EXT.test(path);
}

function normalizeAssetPath(path) {
  return String(path || '').replace(/\\/g, '/').toLowerCase();
}

const featuredMediaEntries = Object.entries(allFeaturedMedia).map(([path, url]) => ({
  path,
  url,
  ext: getExtension(url) || getPathExtension(path)
}));

function findMediaByPath(targetPath) {
  const normalized = normalizeAssetPath(targetPath);
  return featuredMediaEntries.find((entry) => normalizeAssetPath(entry.path) === normalized) || null;
}

function findMediaByBase(basePath) {
  const normalized = normalizeAssetPath(basePath);
  return featuredMediaEntries.find((entry) => normalizeAssetPath(stripExtension(entry.path)) === normalized) || null;
}

function resolveBlogThumbnail(blogPath, thumbnailMeta) {
  if (!blogPath) return null;
  const dir = blogPath.replace(/\/[^/]+$/, '');
  const name = typeof thumbnailMeta === 'string' ? thumbnailMeta.trim() : '';
  if (name) {
    const fileName = name.replace(/^\.\//, '').split(/[/\\]/).pop();
    if (!fileName) return null;
    const match = findMediaByPath(`${dir}/${fileName}`);
    return match ? { url: match.url, ext: match.ext } : null;
  }

  const companion = findMediaByBase(blogPath.replace(/\.md$/i, ''));
  return companion ? { url: companion.url, ext: companion.ext } : null;
}

function stripExtension(path) {
  const dot = path.lastIndexOf('.');
  return dot >= 0 ? path.slice(0, dot) : path;
}

export function getFeaturedEntries(category) {
  const descriptionByBasePath = Object.fromEntries(
    Object.entries(allFeaturedText)
      .filter(([path]) => !isBlogPath(path))
      .map(([path, raw]) => {
        const base = stripExtension(path);
        const text = typeof raw === 'string' ? raw.trim() : '';
        const ext = getPathExtension(path);
        return [base, { text, ext }];
      })
  );

  const blogByBasePath = Object.fromEntries(
    Object.entries(allFeaturedText)
      .filter(([path]) => isBlogPath(path))
      .map(([path, raw]) => {
        const base = path.replace(/\.blog\.md$/i, '');
        const text = typeof raw === 'string' ? raw.trim() : '';
        return [base, { text, path }];
      })
  );

  return Object.entries(allFeaturedMedia)
    .filter(([path]) => path.includes(`/featured/${category}/`) && !isBlogMediaPath(path))
    .map(([path, url]) => {
      const name = getFileName(path);
      const ext = getExtension(url);
      const base = stripExtension(path);
      const descInfo = descriptionByBasePath[base] || null;
      const extracted = descInfo ? extractWorkTags(descInfo.text) : { game: '', date: '', body: '' };
      const descHtml = extracted.body ? renderMarkdownToHtml(extracted.body, descInfo.ext) : '';
      const primaryUrl = extracted.body ? extractPrimaryUrl(extracted.body) : null;
      const blogInfo = blogByBasePath[base] || null;
      return {
        path,
        url,
        name,
        ext,
        descHtml,
        primaryUrl,
        blogRaw: blogInfo?.text || null,
        blogPath: blogInfo?.path || null,
        game: extracted.game,
        date: extracted.date
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function findFeaturedWork(category, name) {
  if (!category || !name) return null;
  return getFeaturedEntries(category).find((entry) => entry.name === name) || null;
}

export function getDevBlog(entry) {
  if (!entry?.blogRaw) return null;
  const { meta, body } = parseFrontmatter(entry.blogRaw);
  const thumbnail = resolveBlogThumbnail(entry.blogPath, meta.thumbnail);
  return {
    title: meta.title || entry.name,
    date: meta.date || '',
    author: meta.author || DEFAULT_BLOG_AUTHOR,
    excerpt: getBlogExcerpt(body),
    html: renderMarkdownToHtml(body, 'md'),
    thumbnailUrl: thumbnail?.url || '',
    thumbnailExt: thumbnail?.ext || ''
  };
}

export function getAllDevBlogs() {
  const posts = Object.keys(FEATURED_CATEGORY_PAGES).flatMap((category) => (
    getFeaturedEntries(category)
      .filter((entry) => entry.blogRaw)
      .map((entry) => {
        const blog = getDevBlog(entry);
        return {
          ...blog,
          name: entry.name,
          category,
          categoryLabel: FEATURED_CATEGORY_PAGES[category].label,
          to: `/dev-blog/${category}/${encodeURIComponent(entry.name)}`
        };
      })
  ));

  return posts.sort((a, b) => {
    const dateA = parseBlogDate(a.date)?.getTime() ?? 0;
    const dateB = parseBlogDate(b.date)?.getTime() ?? 0;
    if (dateB !== dateA) return dateB - dateA;
    return a.title.localeCompare(b.title);
  });
}
